import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { fetchGuides } from '@/services/guides.js';
import { fetchGuideTariffSnapshots } from '@/services/tariffWorkbench.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return 'Sin dato';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin dato';
  return new Intl.DateTimeFormat('es-AR', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

export default function RevalorizacionDeEnvios() {
  const navigate = useNavigate();
  const { tenant, token } = useAuth();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await fetchGuides({
        filters: search ? { quick_search: search } : {},
        page: 1,
        limit: 12,
      });

      const guides = Array.isArray(payload?.data) ? payload.data : [];
      const enriched = await Promise.all(
        guides.map(async (item) => {
          try {
            const snapshots = await fetchGuideTariffSnapshots({ tenant, token, guiaId: item.id });
            return {
              ...item,
              snapshots,
            };
          } catch {
            return {
              ...item,
              snapshots: null,
            };
          }
        }),
      );

      setRows(enriched);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las guías.');
    } finally {
      setLoading(false);
    }
  }, [search, tenant, token]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.88)] px-6 py-5 shadow-[0_18px_48px_rgba(var(--shadow),0.12)]">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[rgba(var(--fg),0.52)]">ST338 legacy-compatible</p>
            <h1 className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">Revalorización de envíos</h1>
            <p className="mt-2 max-w-3xl text-sm text-[rgba(var(--fg),0.7)]">
              Esta vista sólo expone guías con snapshot valorizado y chequea bloqueos por foja, reparto, cobranza y facturación antes de abrir la revalorización.
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={loadRows} className="gap-2" disabled={loading}>
              <RefreshCw size={16} aria-hidden="true" />
              Refrescar
            </Button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <label className="min-w-[280px] flex-1">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">Buscar guía / cliente</span>
            <div className="flex items-center gap-2 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.55)] px-3">
              <Search size={16} aria-hidden="true" className="text-[rgba(var(--fg),0.55)]" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Número, remitente o destinatario"
                className="h-11 w-full bg-transparent text-sm outline-none"
              />
            </div>
          </label>
          <Button type="button" onClick={loadRows} disabled={loading} className="self-end">
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
        </div>
      </section>

      {error ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4">
        {rows.map((row) => {
          const eligibility = row.snapshots?.eligibility;
          const current = row.snapshots?.current;
          return (
            <article
              key={row.id}
              className="rounded-[26px] border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.86)] px-5 py-4 shadow-[0_12px_34px_rgba(var(--shadow),0.08)]"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-[rgba(var(--border),0.45)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.66)]">
                      {row.nro_guia || row.numero_guia || `#${row.id}`}
                    </span>
                    <span className="rounded-full border border-[rgba(var(--border),0.45)] px-3 py-1 text-xs text-[rgba(var(--fg),0.66)]">
                      {row.estado || 'Sin estado'}
                    </span>
                  </div>
                  <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">
                    {row.remitente || row.cliente || 'Sin remitente'} → {row.destinatario || 'Sin destinatario'}
                  </h2>
                  <p className="text-sm text-[rgba(var(--fg),0.68)]">
                    Total actual {formatCurrency(current?.totalFinal || row.total_valorizado || row.total || 0)} · Última valorización {formatDateTime(current?.createdAt || row.valorizacion_snapshot_at)}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <Button
                    type="button"
                    onClick={() => navigate(`/guias/${row.id}/valorizar`)}
                    disabled={eligibility && !eligibility.allowed}
                  >
                    Abrir revalorización
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate(`/guias/${row.id}`)}>
                    Ver guía
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.58)] px-4 py-3 text-sm">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.54)]">Snapshot</p>
                  <p className="mt-2 font-semibold text-[rgb(var(--fg))]">{current?.source || 'Sin dato'}</p>
                  <p className="mt-1 text-[rgba(var(--fg),0.68)]">{formatDateTime(current?.createdAt || row.valorizacion_snapshot_at)}</p>
                </div>
                <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.58)] px-4 py-3 text-sm">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.54)]">Facturación</p>
                  <p className="mt-2 font-semibold text-[rgb(var(--fg))]">{Number(row.snapshots?.downstream?.facturacion?.count || 0)} vínculo(s)</p>
                  <p className="mt-1 text-[rgba(var(--fg),0.68)]">{formatDateTime(row.snapshots?.downstream?.facturacion?.latestFecha)}</p>
                </div>
                <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.58)] px-4 py-3 text-sm">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.54)]">Cobranza</p>
                  <p className="mt-2 font-semibold text-[rgb(var(--fg))]">{Number(row.snapshots?.downstream?.cobranza?.count || 0)} aplicación(es)</p>
                  <p className="mt-1 text-[rgba(var(--fg),0.68)]">Aplicado {formatCurrency(row.snapshots?.downstream?.cobranza?.totalAplicado || 0)}</p>
                </div>
              </div>

              {eligibility?.allowed ? (
                <div className="mt-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700">
                  Habilitada para revalorizar: la guía sigue en estado emitida y no tiene consumos posteriores detectados.
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-700">
                  {eligibility?.message || 'No hay evidencia suficiente para habilitar la revalorización.'}
                </div>
              )}
            </article>
          );
        })}

        {!loading && rows.length === 0 ? (
          <div className="rounded-[26px] border border-dashed border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.72)] px-5 py-8 text-center text-sm text-[rgba(var(--fg),0.62)]">
            No se encontraron guías para revisar.
          </div>
        ) : null}
      </div>
    </div>
  );
}
